import React, { useState } from "react";
import { Row, Col, Table, Badge, ProgressBar, Button } from "react-bootstrap";
import Plot from "react-plotly.js";

const SystemsConnectionByCreationYear = ({ data }) => {

  const [mode, setMode] = useState("count");

  if (!data) return null;

  const devices = JSON.parse(data);
  const devicesArray = Array.isArray(devices) ? devices : Object.values(devices);

  const laserNames = ["LaserGenius +", "Platino Linear"];

  const colors = {
    active: "#2ca02c",
    inactive: "#ff7f0e",
    never: "#d62728"
  };

  const stateLabels = {
    active: "Active",
    inactive: "Inactive",
    never: "Never Active"
  };

  const states = ["active","inactive","never"];

  // -------- YEAR EXTRACTION --------

  const normalized = devicesArray.map(d => {

    let created = d.created || "";

    if (!created.includes(".")) {
      created = created + ".0000000";
    }

    const year = new Date(created).getFullYear();

    return {
      ...d,
      createdYear: year
    };

  });

  // -------- SPLIT DATASETS --------

  const allDevices = normalized;

  const laserDevices = normalized.filter(d =>
    laserNames.includes(d.device)
  );

  const automationDevices = normalized.filter(d =>
    !laserNames.includes(d.device)
  );

  // -------- AGGREGATION --------

  const aggregate = (dataset) => {

    const map = {};

    dataset.forEach(d => {

      const year = d.createdYear;

      if (!map[year]) {
        map[year] = { active:0, inactive:0, never:0 };
      }

      if (d.stato === "active") map[year].active++;
      if (d.stato === "inactive") map[year].inactive++;
      if (d.stato === "never active") map[year].never++;

    });

    return map;
  };

  const allAgg = aggregate(allDevices);
  const laserAgg = aggregate(laserDevices);
  const autoAgg = aggregate(automationDevices);

  const datasets = [
    { name:"All Devices", agg: allAgg },
    { name:"Laser Devices", agg: laserAgg },
    { name:"Automation Devices", agg: autoAgg }
  ];

  const years = Object.keys(allAgg)
    .map(Number)
    .sort((a,b)=>a-b);

  // -------- PERCENT --------

  const percent = (v,total)=>{
    if(!total) return 0;
    return ((v/total)*100).toFixed(1);
  };

  // -------- PLOT --------

  const traces = [];

  const cols = 3;
  const rows = 1;

  datasets.forEach((ds,i)=>{

    states.forEach(state=>{

      const yValues = years.map(year=>{

        const total =
          (ds.agg[year]?.active||0) +
          (ds.agg[year]?.inactive||0) +
          (ds.agg[year]?.never||0);

        const value = ds.agg[year]?.[state] || 0;

        return mode==="percent" && total
          ? (value/total)*100
          : value;

      });

      traces.push({

        x: years,
        y: yValues,

        type:"bar",

        marker:{color:colors[state]},

        name: stateLabels[state],
        legendgroup: state,

        showlegend: i===0,

        xaxis:"x"+(i+1),
        yaxis:"y"+(i+1)

      });

    });

  });

    const layout = {

    grid:{
        rows:rows,
        columns:cols,
        pattern:"independent"
    },

    barmode:"stack",

    height:450,

    legend:{
        orientation:"h",
        x:0.5,
        xanchor:"center",
        y:-0.1
    },

    margin:{t:80,l:60,r:20,b:60},

    xaxis:{
        tickmode:"array",
        tickvals:years,
        ticktext:years
    },

    xaxis2:{
        tickmode:"array",
        tickvals:years,
        ticktext:years
    },

    xaxis3:{
        tickmode:"array",
        tickvals:years,
        ticktext:years
    },

    annotations:datasets.map((ds,i)=>({
        text:`<b>${ds.name}</b>`,
        xref:"x"+(i+1)+" domain",
        yref:"y"+(i+1)+" domain",
        x:0.5,
        y:1.15,
        showarrow:false,
        font: { 
          size: 18,
          family: "Comfortaa, sans serif",
          }
    }))

    };

  return(

  <>

  {/* TITLE */}

  <Row className="mt-1 mb-4">
    <Col md={{ span: 8, offset: 2 }}>
      <h4 className="text-center">
        Analisi per anno di creazione
      </h4>
    </Col>
  </Row>

  {/* MODE BUTTONS */}

  <Row className="mb-3">
    <Col className="text-center">

      <Button
        variant={mode==="count"?"primary":"outline-primary"}
        onClick={()=>setMode("count")}
        className="me-2"
      >
        Count
      </Button>

      <Button
        variant={mode==="percent"?"primary":"outline-primary"}
        onClick={()=>setMode("percent")}
      >
        %
      </Button>

    </Col>
  </Row>

  {/* CHART */}

  <Row>
    <Col>
      <Plot
        data={traces}
        layout={layout}
        style={{ width:"100%" }}
        config={{ responsive:true }}
      />
    </Col>
  </Row>

  {/* TABLE DASHBOARD */}

  <Row className="mt-4">
    <Col md={{ span:10, offset:1 }}>

      <Table striped bordered hover>

        <thead>
          <tr className="text-center">
            <th>Year</th>
            <th>Active</th>
            <th>Inactive</th>
            <th>Never Active</th>
            <th>Distribution</th>
          </tr>
        </thead>

        <tbody>

        {years.map(year=>{

          const active = allAgg[year]?.active || 0;
          const inactive = allAgg[year]?.inactive || 0;
          const never = allAgg[year]?.never || 0;

          const total = active+inactive+never;

          const pActive = percent(active,total);
          const pInactive = percent(inactive,total);
          const pNever = percent(never,total);

          return(

          <tr key={year} className="text-center align-middle">

            <td>
              <b>{year}</b>
              <div style={{fontSize:12,color:"#777"}}>
                {total} devices
              </div>
            </td>

            <td>
              <Badge bg="success">
                {active} ({pActive}%)
              </Badge>
            </td>

            <td>
              <Badge bg="warning" text="dark">
                {inactive} ({pInactive}%)
              </Badge>
            </td>

            <td>
              <Badge bg="danger">
                {never} ({pNever}%)
              </Badge>
            </td>

            <td style={{minWidth:260}}>

              <ProgressBar>

                <ProgressBar variant="success" now={pActive} key={1}/>
                <ProgressBar variant="warning" now={pInactive} key={2}/>
                <ProgressBar variant="danger" now={pNever} key={3}/>

              </ProgressBar>

            </td>

          </tr>

          )

        })}

        </tbody>

      </Table>

    </Col>
  </Row>

  </>

  );

};

export default SystemsConnectionByCreationYear;